import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { getChartType } from './chartTheme';
import { ChartFilterDefs } from './ChartFilterDefs';
import { CURSOR } from './interactions/cursorStates';

/**
 * ChartShell — the single wrapper every Data Vault chart renders inside.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────┐
 *   │  Title [pill] [NEW]                                  │  ← persistent strip
 *   ├──────────────────────────────────────────────────────┤
 *   │                                                      │
 *   │              CHART BODY (full-bleed)                 │
 *   │                                                      │
 *   ├═════════════════════════════════════════════════════ ┤  ← south-resize handle
 *   └──────────────────────────────────────────────────────┘
 *                                                       ↘
 *                                              SE corner handle
 *
 * The strip carries only the chart name (+ category pill + NEW badge on hover).
 * The previously-mounted ModeBar / aspect-preset / export controls were
 * removed — they were a mix of mode-toggle no-ops, button-driven actions
 * that the SVG didn't reflect, and chrome that competed with chart-internal
 * controls. Wheel-zoom + drag-pan still come live via `useChartViewport`
 * attached to each chart's SVG; they don't need a button to fire.
 *
 * Sizing: drag the south edge (or the SE corner) to resize the card.
 * Without a user-set drag, the card uses `minHeight` as a floor and grows
 * to fit content so the chart is never clipped. User-driven height is
 * persisted to localStorage keyed by chartId.
 */

interface Props {
  chartId: string | null;
  children: ReactNode;
  /** Height floor; the card grows past this to fit content. */
  minHeight?: number;
}

const HEIGHT_STORAGE_PREFIX = 'argos.chartShell.height.';
const HEIGHT_MIN = 180;
const HEIGHT_MAX = 1400;

function loadStoredHeight(chartId: string | null): number | null {
  if (!chartId) return null;
  try {
    const raw = localStorage.getItem(`${HEIGHT_STORAGE_PREFIX}${chartId}`);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= HEIGHT_MIN && n <= HEIGHT_MAX ? n : null;
  } catch {
    return null;
  }
}

function saveStoredHeight(chartId: string | null, height: number): void {
  if (!chartId) return;
  try {
    localStorage.setItem(`${HEIGHT_STORAGE_PREFIX}${chartId}`, String(height));
  } catch {
    // localStorage unavailable / quota — silent fallback.
  }
}

export function ChartShell({ chartId, children, minHeight = 360 }: Props) {
  const chartDef = chartId ? getChartType(chartId) : null;
  const [userHeight, setUserHeight] = useState<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Restore persisted height when the chart changes.
  useEffect(() => {
    setUserHeight(loadStoredHeight(chartId));
  }, [chartId]);

  // ── Resize handles ───────────────────────────────────────────────────
  // Width is parent-driven (FilterRibbon at top, ChartShell fills the row).
  // Height is user-driven via drag on the south edge or SE corner.

  const beginResize = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!cardRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const startY = e.clientY;
      const startHeight = cardRef.current.getBoundingClientRect().height;
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);

      const onMove = (ev: PointerEvent) => {
        const dy = ev.clientY - startY;
        const next = Math.min(
          HEIGHT_MAX,
          Math.max(HEIGHT_MIN, Math.round(startHeight + dy)),
        );
        setUserHeight(next);
      };
      const onUp = () => {
        target.releasePointerCapture(e.pointerId);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (cardRef.current) {
          const finalHeight = Math.round(
            cardRef.current.getBoundingClientRect().height,
          );
          saveStoredHeight(chartId, finalHeight);
        }
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [chartId],
  );

  if (!chartId || !chartDef) {
    return (
      <div
        className="rounded-xl border p-8 flex items-center justify-center min-h-[400px]"
        style={{
          background: 'var(--dv-bg-card)',
          borderColor: 'var(--dv-border-subtle)',
        }}
      >
        <div className="text-center space-y-3">
          <div className="text-3xl opacity-30">&#128202;</div>
          <p
            className="text-sm tracking-wider"
            style={{ color: 'var(--dv-text-muted)' }}
          >
            Select a visualization from the sidebar
          </p>
          <p
            className="text-xs"
            style={{ color: 'var(--dv-text-disabled)' }}
          >
            70 chart types across 10 categories
          </p>
        </div>
      </div>
    );
  }

  // Sizing:
  //   - If the user explicitly resized via drag-handle, pin the height
  //     (fixed) — that's their stated intent.
  //   - Otherwise, use minHeight as a FLOOR so the card grows to fit the
  //     chart content. This prevents the chart from being clipped behind
  //     the card when its internal layout is taller than the default.
  const cardStyle: React.CSSProperties =
    userHeight !== null
      ? {
          background: 'var(--dv-bg-card)',
          borderColor: 'var(--dv-border-subtle)',
          height: userHeight,
        }
      : {
          background: 'var(--dv-bg-card)',
          borderColor: 'var(--dv-border-subtle)',
          minHeight,
        };

  return (
    <div
      ref={cardRef}
      className="group relative rounded-xl border transition-shadow duration-200 hover:shadow-lg flex flex-col"
      style={cardStyle}
    >
      {/* Persistent top strip — title (+ hover-revealed category pill + NEW). */}
      <div className="flex items-center justify-between px-3 h-7 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <h3
            className="text-xs font-medium tracking-wide truncate"
            style={{ color: 'var(--dv-text-secondary)', opacity: 0.85 }}
          >
            {chartDef.name}
          </h3>
          <span
            className="text-[9px] px-1.5 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            style={{
              background: 'var(--dv-pill-bg)',
              color: 'var(--dv-text-muted)',
              border: '1px solid var(--dv-border-subtle)',
            }}
          >
            {chartDef.category}
          </span>
          {chartDef.isNew && (
            <span
              className="text-[8px] font-bold tracking-wider px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              style={{
                background: 'rgba(16, 185, 129, 0.1)',
                color: 'var(--dv-accent-emerald)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
              }}
            >
              NEW
            </span>
          )}
        </div>
      </div>

      {/* Chart body — padded so chart content (axis labels, internal
          headers) has breathing room from the card edges. */}
      <div
        ref={bodyRef}
        className="flex-1 overflow-auto rounded-b-xl px-4 pb-4 pt-1"
      >
        {/* Shared SVG <defs> for hover halo, MCL glow, point glow. Mounted
            once per ChartShell so every chart inside can reference the
            filters by URL id. */}
        <ChartFilterDefs />
        {children}
      </div>

      {/* South-edge resize handle — invisible 6px lip, cursor-driven. */}
      <div
        onPointerDown={beginResize}
        className="absolute left-0 right-6 bottom-0 h-1.5 z-20"
        style={{ touchAction: 'none', cursor: CURSOR.resizeNS }}
        title="Drag to resize height"
      />
      {/* SE corner resize handle — visible on hover. */}
      <div
        onPointerDown={beginResize}
        className="absolute right-0 bottom-0 w-4 h-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ touchAction: 'none', cursor: CURSOR.resizeNWSE }}
        title="Drag to resize height"
      >
        <svg viewBox="0 0 16 16" className="w-full h-full" style={{ color: 'var(--dv-text-disabled)' }}>
          <path d="M11 15 L15 11 M7 15 L15 7 M3 15 L15 3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}
